import {
  EndpointBuilder,
  QueryDefinition,
  MutationDefinition,
  BaseQueryFn,
} from '@reduxjs/toolkit/query';

import type { IShiftApplication } from 'interfaces/ShiftApplication.interface';
import type { ICollectionApiResponse } from 'services/api/api.types';
import type {
  IShiftApplicationPostArgs,
  IGetShiftApplicationsResponse,
  IGetShiftApplicationsCollectionArgs,
  IShiftApplicationDeleteArgs,
  IShiftApplicationUpdateArgs,
} from './shiftApplications.api.types';
import {
  serializeFilters,
  serializeFlatObject,
} from 'services/helper/getPaginationParams.helper';
import { transformCollectionResponse } from 'services/teamHeroApi.helpers';

export const getShiftApplications = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): QueryDefinition<
  IGetShiftApplicationsCollectionArgs,
  BaseQueryFn,
  string,
  IGetShiftApplicationsResponse
> =>
  build.query<
    IGetShiftApplicationsResponse,
    IGetShiftApplicationsCollectionArgs
  >({
    providesTags: ['ShiftApplication'],
    query: (queryArg: IGetShiftApplicationsCollectionArgs) => {
      const paginationParamObject = {
        page: queryArg.page,
        itemsPerPage: queryArg.itemsPerPage,
        pagination: queryArg.pagination,
        ...queryArg.order,
      };
      const paginationQuery = serializeFlatObject(paginationParamObject, '?');
      const filterQuery = serializeFilters(queryArg.filters);

      return {
        url: `api/project_shift_applications${paginationQuery}${filterQuery}`,
      };
    },
    transformResponse: (
      response: ICollectionApiResponse<IShiftApplication>
    ): IGetShiftApplicationsResponse =>
      transformCollectionResponse<IShiftApplication>(response),
  });

export const postApplicationShiftItem = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): MutationDefinition<
  IShiftApplicationPostArgs,
  BaseQueryFn,
  string,
  IShiftApplication
> =>
  build.mutation<IShiftApplication, IShiftApplicationPostArgs>({
    invalidatesTags: ['ShiftApplication'],
    query: (queryArg) => ({
      url: `/api/project_shift_applications`,
      method: 'POST',
      body: queryArg.body,
    }),
  });

export const deleteApplicationShiftItem = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): MutationDefinition<
  IShiftApplicationDeleteArgs,
  BaseQueryFn,
  string,
  IShiftApplication
> =>
  build.mutation<IShiftApplication, IShiftApplicationDeleteArgs>({
    invalidatesTags: ['ShiftApplication'],
    query: (queryArg) => ({
      url: `/api/project_shift_applications/${queryArg.id}`,
      method: 'DELETE',
    }),
  });

export const putApplicationShiftItem = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): MutationDefinition<
  IShiftApplicationUpdateArgs,
  BaseQueryFn,
  string,
  IShiftApplication
> =>
  build.mutation<IShiftApplication, IShiftApplicationUpdateArgs>({
    invalidatesTags: (_result, _error, args) =>
      args.skipInvalidateTags ? [] : ['ShiftApplication'],
    query: (queryArg) => ({
      url: `/api/project_shift_applications/${queryArg.id}`,
      method: 'PATCH',
      body: queryArg.body,
    }),
  });
